import React, { useState, useEffect } from 'react'
import { Image } from 'react-native'
import {
  Settings, Users, BarChart3, FolderTree, Share2,
  ChevronRight, ArrowLeft, Store, Globe, LogOut, HelpCircle,
} from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import {
  TopBar, IconButton, ListItem, Card, Text, Badge, BadgeText,
  Divider, YStack, XStack, Theme, ScrollView,
} from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MerchantMore'>
  route: { params: { storeId: string } }
}
interface StoreInfo { id: string; name: string; store_type: string; logo_url?: string }
interface MenuItem {
  key: string; icon: React.ComponentType<{ size: number; color: string }>
  labelKey: string; route?: keyof RootStackParamList; onPress?: () => void
  description?: string; badge?: string
}

export default function MerchantMoreScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t, language, setLanguage } = useLanguage()
  const { signOut } = useAuth()
  const [store, setStore] = useState<StoreInfo | null>(null)

  useEffect(() => { loadStoreInfo() }, [storeId])

  const loadStoreInfo = async () => {
    const { data } = await supabase.from('flea_markets').select('id, name, store_type, logo_url').eq('id', storeId).single()
    if (data) setStore(data)
  }

  const handleLogout = async () => {
    await signOut()
    navigation.reset({ index: 0, routes: [{ name: 'Login' }] })
  }

  const navigateTo = (r: keyof RootStackParamList, params?: any) => {
    navigation.navigate(r as any, params || { storeId })
  }

  const menuSections: { title: string; items: MenuItem[] }[] = [
    {
      title: t('store_management') || 'Store Management',
      items: [
        { key: 'settings', icon: Settings, labelKey: 'store_settings', route: 'StoreSettings', description: t('store_settings_desc') || 'Name, hours, contact info' },
        { key: 'staff', icon: Users, labelKey: 'staff_management', route: 'MerchantStaff', description: t('staff_desc') || 'Manage team members and roles', badge: t('coming_soon') || 'Soon' },
        { key: 'categories', icon: FolderTree, labelKey: 'categories', route: 'CategoryManagement', description: t('categories_desc') || 'Organize your products' },
        { key: 'social', icon: Share2, labelKey: 'Sosiaalinen media', route: 'SocialAccounts', description: 'Tilit, julkaisut ja jakaminen' },
      ],
    },

    {
      title: t('reports_analytics') || 'Reports & Analytics',
      items: [
        { key: 'reports', icon: BarChart3, labelKey: 'reports', route: 'InventoryReports', description: t('reports_desc') || 'Sales and inventory analytics' },
      ],
    },
    {
      title: t('account') || 'Account',
      items: [
        { key: 'switch_store', icon: Store, labelKey: 'switch_store', onPress: () => navigation.navigate('MerchantStoreSelect'), description: t('switch_store_desc') || 'Manage another store' },
        { key: 'language', icon: Globe, labelKey: 'language', onPress: () => setLanguage(language === 'en' ? 'fi' : 'en'), description: language === 'en' ? 'English' : 'Suomi' },
        { key: 'help', icon: HelpCircle, labelKey: 'help_support', onPress: () => {}, description: t('help_desc') || 'Get help and support' },
        { key: 'logout', icon: LogOut, labelKey: 'logout', onPress: handleLogout, description: t('logout_desc') || 'Sign out of your account' },
      ],
    },
  ]

  return (
    <Theme name="brick">
      <YStack f={1} bg="$background">
        <TopBar
          title={t('more') || 'More'}
          left={
            <IconButton variant="ghost" onPress={() => navigation.navigate('MerchantDashboard', { storeId })}>
              <ArrowLeft size={20} color="$colorPrimary" />
            </IconButton>
          }
        />
        <ScrollView f={1}>
          {store && (
            <Card variant="elevated" mx="$md" mt="$md" pressable onPress={() => navigateTo('StoreSettings')}>
              <XStack ai="center" gap="$md">
                {store.logo_url ? (
                  <Image source={{ uri: store.logo_url }} style={{ width: 56, height: 56, borderRadius: 12 }} />
                ) : (
                  <YStack w={56} h={56} br="$lg" bg="rgba(192,122,95,0.1)" jc="center" ai="center">
                    <Store size={24} color="#C07A5F" />
                  </YStack>
                )}
                <YStack f={1}>
                  <Text preset="h3">{store.name}</Text>
                  <Text preset="caption" tone="secondary" mt={2}>{t(store.store_type) || store.store_type}</Text>
                </YStack>
                <ChevronRight size={20} color="#858B80" />
              </XStack>
            </Card>
          )}

          {menuSections.map((section, si) => (
            <YStack key={si} mt="$md" px="$md">
              <Text preset="caption" tone="muted" mb="$sm" ml={4} textTransform="uppercase" letterSpacing={0.5}>
                {section.title}
              </Text>
              <Card variant="flat">
                {section.items.map((item, ii) => {
                  const Icon = item.icon
                  const isLogout = item.key === 'logout'
                  return (
                    <React.Fragment key={item.key}>
                      {ii > 0 && <Divider />}
                      <ListItem
                        icon={
                          <YStack w={40} h={40} br={10} bg={isLogout ? 'rgba(211,47,47,0.1)' : 'rgba(192,122,95,0.1)'} jc="center" ai="center">
                            <Icon size={20} color={isLogout ? '#D32F2F' : '#C07A5F'} />
                          </YStack>
                        }
                        title={t(item.labelKey) || item.labelKey}
                        subtitle={item.description}
                        right={
                          item.badge ? (
                            <Badge variant="warning" size="sm"><BadgeText variant="warning" size="sm">{item.badge}</BadgeText></Badge>
                          ) : (
                            <ChevronRight size={18} color="#858B80" />
                          )
                        }
                        onPress={() => {
                          if (item.onPress) item.onPress()
                          else if (item.route) navigateTo(item.route)
                        }}
                      />
                    </React.Fragment>
                  )
                })}
              </Card>
            </YStack>
          ))}

          <Text preset="caption" tone="muted" textAlign="center" my="$xl">Fliisi v1.0.0</Text>
        </ScrollView>
      </YStack>
    </Theme>
  )
}
